/**
 * Ideas and bugs (docs/feedback.md). Owner, 2026-10-23: "Add a feature request button below the
 * version information where users can submit feature requests and bugs", then "Have it be a native
 * form that can have text up to 1000 chars and up to 3 images", then "I don't need to see the
 * requests in dev tools, it just has to be easily accessible by you" (Claude, from the terminal:
 * `npm run feedback`).
 *
 * Pure rules and copy, read by the server (functions/api/feedback.ts) and both clients (the web's
 * src/components/FeedbackSheet.tsx, the app's screens/Feedback.tsx): the limits, the kinds, every
 * line the form says, and the check the server runs on what it is sent.
 */
import { isPhotoId } from "./notes.ts";

export type FeedbackKind = "idea" | "bug";
export type FeedbackStatus = "new" | "seen" | "done";
/** Which client sent it. */
export type FeedbackClient = "web" | "ios" | "android";

/** The most a request may say, in characters (code points, as the counter counts). */
export const FEEDBACK_MAX_CHARS = 1000;
/** The most photos one request carries. */
export const FEEDBACK_MAX_PHOTOS = 3;
/** The most requests one household sends in a day (UTC); the next one is refused until tomorrow. */
export const FEEDBACK_DAILY_LIMIT = 20;
/** The longest a context field (version, route, user agent) is kept, in characters. */
export const FEEDBACK_CONTEXT_MAX = 300;

export const FEEDBACK_KINDS: readonly FeedbackKind[] = ["idea", "bug"];
export const FEEDBACK_STATUSES: readonly FeedbackStatus[] = ["new", "seen", "done"];
export const FEEDBACK_CLIENTS: readonly FeedbackClient[] = ["web", "ios", "android"];

/** The segmented toggle, as the weekly scroll's Appreciation / Accomplishment. */
export const FEEDBACK_KIND_OPTIONS: readonly { value: FeedbackKind; label: string }[] = [
  { value: "idea", label: "Idea" },
  { value: "bug", label: "Bug" },
];

/** The button under About's version, and the form's title. */
export const FEEDBACK_BUTTON = "Suggest something";
export const FEEDBACK_TITLE = "Suggest something";
export const FEEDBACK_PLACEHOLDER: Readonly<Record<FeedbackKind, string>> = {
  idea: "Me wish the app could...",
  bug: "What broke? What did you tap?",
};
/** The screen reader's name for the text box. */
export const FEEDBACK_FIELD_LABEL: Readonly<Record<FeedbackKind, string>> = { idea: "Your idea", bug: "What went wrong" };
/** The tiny muted line under the form: the one word on what rides along. */
export const FEEDBACK_CONTEXT_LINE = "Sends your app version with it.";
export const FEEDBACK_ADD_PHOTO = "Add a photo";
export const FEEDBACK_REMOVE_PHOTO = "Remove photo";
export const FEEDBACK_SEND = "Send it";
export const FEEDBACK_SENDING = "Sending…";
export const FEEDBACK_CANCEL = "Cancel";
/** After Send: the peon's line, on the toast, as the form closes. */
export const FEEDBACK_SENT = "Me tell the boss.";
export const FEEDBACK_MISSING = "Say something first.";
export const FEEDBACK_TOO_LONG = "That is too long.";
export const FEEDBACK_TOO_MANY_PHOTOS = `${FEEDBACK_MAX_PHOTOS} photos at most.`;
export const FEEDBACK_PHOTO_FAILED = "That photo didn't upload. Try again.";
export const FEEDBACK_RATE_LIMITED = "The boss has enough to read today. Try again tomorrow.";

/** The counter under the box: "12 / 1000". */
export function feedbackCount(draft: string): string {
  return `${Array.from(draft).length} / ${FEEDBACK_MAX_CHARS}`;
}

/** How many more photos fit, for the library's multi-select. */
export function photosLeft(count: number): number {
  return Math.max(0, FEEDBACK_MAX_PHOTOS - count);
}

/** The draft cut to the limit, in code points (a pasted wall of text keeps its first 1000). */
export function clampFeedback(draft: string): string {
  const chars = Array.from(draft);
  return chars.length > FEEDBACK_MAX_CHARS ? chars.slice(0, FEEDBACK_MAX_CHARS).join("") : draft;
}

/** What Send still needs, in the form-missing style (shared/form-missing.ts): null when it can go. */
export function feedbackMissing(f: { draft: string }): { field: "body"; line: string } | null {
  if (!f.draft.trim()) return { field: "body", line: FEEDBACK_MISSING };
  if (Array.from(f.draft).length > FEEDBACK_MAX_CHARS) return { field: "body", line: FEEDBACK_TOO_LONG };
  return null;
}

/** What the client knows about itself, sent with the request and kept as `context_json`. */
export interface FeedbackContext {
  client: FeedbackClient;
  /** The app's version ("1.5.0") or the web's service worker version / build stamp. */
  version: string;
  /** The running over-the-air update's id, in the apps. */
  update_id?: string | null;
  /** The native runtime version, in the apps. */
  runtime?: string | null;
  /** The bundle's build stamp. */
  build?: string | null;
  user_agent?: string | null;
  /** Where the form was opened from (the route under the sheet). */
  route?: string | null;
}

/** POST /api/feedback's body. */
export interface FeedbackDraft {
  kind: FeedbackKind;
  body: string;
  photo_ids: string[];
  context: FeedbackContext;
}

/** POST /api/feedback answers the stored row's id and the line to show. */
export interface FeedbackSent {
  id: string;
  line: string;
}

/** The server's reading of a request: what it keeps, or the words it refuses with (400). */
export type FeedbackCheck =
  | { ok: true; kind: FeedbackKind; body: string; photo_ids: string[]; client: FeedbackClient; version: string; context: Record<string, string> }
  | { ok: false; error: string };

const CONTEXT_KEYS = ["update_id", "runtime", "build", "user_agent", "route"] as const;

function cut(s: string, max = FEEDBACK_CONTEXT_MAX): string {
  return s.length > max ? s.slice(0, max) : s;
}

/**
 * Checks a POSTed request: a kind, words (trimmed, 1 to 1000 characters), 0 to 3 distinct photo ids
 * of the photo store's shape, a known client. The context is kept loosely: only known string fields,
 * each cut to 300 characters, so a client can never fail a request over its own facts. Whether the
 * photos are this household's and unused is the server's to check (server/feedback.ts).
 */
export function checkFeedback(input: unknown): FeedbackCheck {
  const b = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  if (b.kind !== "idea" && b.kind !== "bug") return { ok: false, error: "Pick Idea or Bug." };
  if (typeof b.body !== "string" || !b.body.trim()) return { ok: false, error: FEEDBACK_MISSING };
  const body = b.body.trim();
  if (Array.from(body).length > FEEDBACK_MAX_CHARS) return { ok: false, error: FEEDBACK_TOO_LONG };
  const rawPhotos = b.photo_ids == null ? [] : b.photo_ids;
  if (!Array.isArray(rawPhotos)) return { ok: false, error: FEEDBACK_PHOTO_FAILED };
  if (rawPhotos.length > FEEDBACK_MAX_PHOTOS) return { ok: false, error: FEEDBACK_TOO_MANY_PHOTOS };
  if (!rawPhotos.every(isPhotoId)) return { ok: false, error: FEEDBACK_PHOTO_FAILED };
  const photo_ids = rawPhotos as string[];
  if (new Set(photo_ids).size !== photo_ids.length) return { ok: false, error: FEEDBACK_PHOTO_FAILED };
  const ctx = (b.context && typeof b.context === "object" ? b.context : {}) as Record<string, unknown>;
  const client = FEEDBACK_CLIENTS.includes(ctx.client as FeedbackClient) ? (ctx.client as FeedbackClient) : "web";
  const version = typeof ctx.version === "string" ? cut(ctx.version.trim(), 100) : "";
  const context: Record<string, string> = {};
  for (const key of CONTEXT_KEYS) {
    const v = ctx[key];
    if (typeof v === "string" && v.trim()) context[key] = cut(v.trim());
  }
  return { ok: true, kind: b.kind, body, photo_ids, client, version, context };
}

/** The first words of a request, for a one-line listing: at most `max` characters, cut at a word. */
export function firstWords(body: string, max = 60): string {
  const flat = body.replace(/\s+/g, " ").trim();
  if (flat.length <= max) return flat;
  const head = flat.slice(0, max - 1);
  const space = head.lastIndexOf(" ");
  return `${(space > max / 2 ? head.slice(0, space) : head).trimEnd()}…`;
}
